/**
 * Monte Carlo AI - "Explorer"
 *
 * Pure MCTS implementation using random playouts to discover good moves.
 * Strengths:
 * - No evaluation function needed
 * - Can discover unexpected tactics
 * - Scales well with time
 * - Handles complex positions naturally
 *
 * Weaknesses:
 * - May miss forced tactical sequences
 * - Requires many iterations for accuracy
 * - Can be unpredictable
 */

import { GameState, IMove, Position, PieceType } from "../../types";
import { IAIPlayer, AIMoveResult, AIStats, AIPlayerType } from "../Core/IAIPlayer";
import { ZodiacEngine } from "../../zodiacEngine";
import { MCTSEngine } from "./MCTSEngine";

export class MonteCarloAI implements IAIPlayer {
  readonly name = AIPlayerType.MCTS;
  readonly displayName = "Explorer";
  readonly description = "Pure MCTS using random playouts to discover strong moves";

  private mctsEngine: MCTSEngine;
  private gameEngine: ZodiacEngine;
  private stats: AIStats = {
    movesCalculated: 0,
    averageThinkingTime: 0,
    nodesEvaluated: 0,
  };
  private totalThinkingTime = 0;
  private totalNodes = 0;

  constructor(gameEngine: ZodiacEngine) {
    this.gameEngine = gameEngine;
    this.mctsEngine = new MCTSEngine();
  }

  /**
   * Select king using random choice (placeholder - will use MCTS simulation later)
   */
  selectKing(availableKings: PieceType[], isLightSide: boolean): PieceType {
    // For now: random selection
    // TODO: Use MCTS to simulate games with each king and pick the one with best win rate
    const randomIndex = Math.floor(Math.random() * availableKings.length);
    return availableKings[randomIndex];
  }

  async selectMove(gameState: GameState, maxThinkingTime: number): Promise<AIMoveResult> {
    const startTime = Date.now();

    // Run MCTS
    const result = this.mctsEngine.findBestMove(gameState, maxThinkingTime);

    if (!result) {
      throw new Error("Explorer AI failed to find a valid move");
    }

    const thinkingTime = Date.now() - startTime;

    // Update stats
    this.stats.movesCalculated++;
    this.totalThinkingTime += thinkingTime;
    this.totalNodes += result.explorationStats.nodesCreated;
    this.stats.averageThinkingTime = this.totalThinkingTime / this.stats.movesCalculated;
    this.stats.nodesEvaluated = this.totalNodes;

    // Confidence based on visits and win rate
    const confidence = this.calculateConfidence(result.visits, result.winRate, result.totalIterations);

    return {
      fromPosition: result.move.fromPosition,
      move: result.move.move,
      confidence,
      thinkingTime,
      evaluation: result.winRate * 1000 - 500, // Convert win rate to evaluation score
      reasoning: `MCTS: ${result.totalIterations} iterations, ${result.visits} visits, ${(
        result.winRate * 100
      ).toFixed(1)}% win rate`,
    };
  }

  getStats(): AIStats {
    return { ...this.stats };
  }

  reset(): void {
    this.stats = {
      movesCalculated: 0,
      averageThinkingTime: 0,
      nodesEvaluated: 0,
    };
    this.totalThinkingTime = 0;
    this.totalNodes = 0;
  }

  configure(config: Record<string, any>): void {
    this.mctsEngine.configure(config);
  }

  /**
   * Calculate confidence based on MCTS statistics
   */
  private calculateConfidence(visits: number, winRate: number, totalIterations: number): number {
    // High visit count indicates consensus
    const visitFactor = Math.min(1.0, visits / (totalIterations * 0.3));

    // Extreme win rates indicate clear advantage
    let winRateFactor = 0.5;
    if (winRate > 0.8) winRateFactor = 0.9;
    else if (winRate > 0.65) winRateFactor = 0.8;
    else if (winRate > 0.55) winRateFactor = 0.7;
    else if (winRate < 0.2) winRateFactor = 0.9; // Clear losing position, but confident about it
    else if (winRate < 0.35) winRateFactor = 0.8;
    else if (winRate < 0.45) winRateFactor = 0.7;

    // More iterations = higher confidence
    const iterationFactor = Math.min(1.0, totalIterations / 5000) * 0.2;

    const confidence = Math.min(0.95, visitFactor * 0.5 + winRateFactor * 0.3 + iterationFactor);

    return Math.max(0.4, confidence); // Minimum 40% confidence
  }
}

export { MCTSEngine } from "./MCTSEngine";
export { MCTSNode } from "./MCTSNode";
export { Simulation } from "./Simulation";